import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import ListingCard from "@/components/ListingCard";
import EventCard from "@/components/EventCard";
import JobCard from "@/components/JobCard";
import NeedCard from "@/components/NeedCard";
import { Search, ArrowRight, ShoppingBag, Sparkles, CalendarDays, Users, Stethoscope, Megaphone } from "lucide-react";

const HERO_IMG = "https://customer-assets-jt897jd0.emergentagent.net/job_african-marketplace-19/artifacts/86c543f631adc8fd_Screenshot%202026-10-31%20at%2012-10-01%20AfriSeattle%20Image%20Concept.png";
const PATTERN_IMG = "https://images.unsplash.com/photo-1614619838981-a5d41d4dda42?crop=entropy&cs=srgb&fm=jpg&w=800&q=80";

const CATEGORIES = [
  { name: "Furniture", icon: "🪑" },
  { name: "Electronics", icon: "📱" },
  { name: "Clothing & Fashion", icon: "👗" },
  { name: "Cultural Goods", icon: "🎨" },
  { name: "Kitchen", icon: "🍲" },
  { name: "Baby & Kids", icon: "🧸" },
  { name: "Free / Giveaway", icon: "🎁" },
  { name: "Books", icon: "📚" },
];

export default function Home() {
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => setData({ featured_listings: [], recent_listings: [], upcoming_events: [], stats: {} }));
  }, []);

  const submitSearch = (e) => {
    e.preventDefault();
    nav(`/post/need?q=${encodeURIComponent(q)}`);
  };

  return (
    <div>
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute inset-0 abm-noise" />
        </div>
        <div className="max-w-7xl mx-auto px-5 sm:px-8 pt-14 pb-16 lg:pt-24 lg:pb-24 grid lg:grid-cols-[1.1fr_0.9fr] gap-10 items-center">
          <div>
            <div className="inline-flex items-center gap-2 abm-chip" data-testid="hero-badge">
              <Sparkles className="w-3 h-3" /> A <span style={{ fontFamily: "'Bebas Neue', sans-serif" }} className="text-2xl sm:text-3xl tracking-[0.1em] text-[#C05A3D] mx-1 align-middle leading-none">SITE</span> for Africans in Washington State
            </div>
            <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02] text-[#1E3A2F]">
              What do you need?<br />
              <span className="text-[#C05A3D]">Somebody</span> in your community<br />
              probably has it.
            </h1>
            <p className="mt-5 text-base sm:text-lg text-[#5C5C5C] max-w-xl leading-relaxed">
              Say it out loud: "I need a caterer." "I have a couch." "I offer haircuts."
              We route your post through the African community across Washington and pull matches
              from listings, events and jobs.
            </p>

            <form onSubmit={submitSearch} className="mt-8 flex items-center gap-2 bg-white rounded-full border border-[#E2DED0] p-1.5 shadow-[0_8px_32px_rgba(30,58,47,0.06)] max-w-xl">
              <Search className="w-4 h-4 ml-3 text-[#5C5C5C]" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="I need a photographer, a mechanic, an apartment…"
                data-testid="hero-search-input"
                className="border-0 focus-visible:ring-0 focus-visible:ring-offset-0 bg-transparent flex-1"
              />
              <Button data-testid="hero-search-submit" type="submit" className="abm-btn-primary rounded-full h-10 px-5">
                Ask
              </Button>
            </form>

            <div className="mt-6 flex flex-wrap items-center gap-3">
              <Link to="/post/need" data-testid="cta-post-need" className="inline-flex items-center gap-2 rounded-full abm-btn-pine px-5 h-11 font-medium">
                <Megaphone className="w-4 h-4" /> Post a need or offer
              </Link>
              <Link to="/post/listing" data-testid="cta-post-listing" className="inline-flex items-center gap-2 rounded-full border border-[#1E3A2F] text-[#1E3A2F] px-5 h-11 font-medium hover:bg-[#1E3A2F] hover:text-white transition">
                <ShoppingBag className="w-4 h-4" /> Sell an item
              </Link>
              <Link to="/post/event" data-testid="cta-post-event" className="inline-flex items-center gap-2 rounded-full text-[#1E3A2F] px-5 h-11 font-medium hover:text-[#C05A3D] transition">
                <CalendarDays className="w-4 h-4" /> Post an event
              </Link>
            </div>

            {data?.stats && (
              <div className="mt-10 grid grid-cols-4 gap-3 max-w-md">
                <Stat label="Listings" value={data.stats.listings || 0} />
                <Stat label="Events" value={data.stats.events || 0} />
                <Stat label="Jobs" value={data.stats.jobs || 0} />
                <Stat label="Members" value={data.stats.members || 0} />
              </div>
            )}
          </div>

          <div className="relative">
            <div className="absolute -top-6 -left-6 w-32 h-32 rounded-2xl abm-kente opacity-70" />
            <div className="absolute -bottom-6 -right-6 w-40 h-40 rounded-2xl overflow-hidden">
              <img src={PATTERN_IMG} alt="" className="w-full h-full object-cover" />
            </div>
            <div className="relative rounded-3xl overflow-hidden shadow-[0_24px_60px_rgba(30,58,47,0.18)] border border-[#E2DED0] bg-white">
              <img src={HERO_IMG} alt="AfriSeattle - Bringing Africans together" data-testid="hero-image" className="w-full h-auto block" />
            </div>
          </div>
        </div>
      </section>

      {/* Recent community needs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold inline-flex items-center gap-2"><Megaphone className="w-3 h-3" /> Community asks</div>
            <h2 className="font-display text-3xl text-[#1E3A2F] mt-1">Who can help this week?</h2>
          </div>
          <Link to="/needs" data-testid="home-needs-all" className="text-sm text-[#1E3A2F] inline-flex items-center gap-1 hover:text-[#C05A3D]">
            All requests <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {data?.recent_needs?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.recent_needs.slice(0, 6).map((n) => <NeedCard key={n.id} need={n} />)}
          </div>
        ) : (
          <div className="text-center py-14 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
            <Megaphone className="w-8 h-8 text-[#C05A3D] mx-auto" />
            <div className="mt-3 font-display text-xl text-[#1E3A2F]">Be the first to ask</div>
            <p className="text-sm text-[#5C5C5C] mt-1 max-w-md mx-auto">Post what you need or what you have — the community can share it and connect the dots.</p>
            <Link to="/post/need" className="inline-block mt-5 rounded-full abm-btn-primary px-5 h-10 leading-10 text-white text-sm">Post to the community</Link>
          </div>
        )}
      </section>

      {/* Categories */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">Browse</div>
            <h2 className="font-display text-3xl text-[#1E3A2F] mt-1">Popular categories</h2>
          </div>
          <Link to="/marketplace" className="text-sm text-[#1E3A2F] inline-flex items-center gap-1 hover:text-[#C05A3D]">
            All items <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {CATEGORIES.map((c) => (
            <Link
              key={c.name}
              to={`/marketplace?category=${encodeURIComponent(c.name)}`}
              data-testid={`category-${c.name}`}
              className="abm-card p-5 flex items-center gap-3 hover:border-[#C05A3D]"
            >
              <div className="w-11 h-11 rounded-xl bg-[#F7F4EB] grid place-items-center text-2xl">{c.icon}</div>
              <div>
                <div className="font-medium text-[#1A1A1A]">{c.name}</div>
                <div className="text-xs text-[#5C5C5C]">Shop this category</div>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* Featured / Recent listings */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold">Marketplace</div>
            <h2 className="font-display text-3xl text-[#1E3A2F] mt-1">Fresh from the community</h2>
          </div>
          <Link to="/marketplace" className="text-sm text-[#1E3A2F] inline-flex items-center gap-1 hover:text-[#C05A3D]">
            Explore marketplace <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        {data?.recent_listings?.length ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {data.recent_listings.slice(0, 8).map((l) => <ListingCard key={l.id} listing={l} />)}
          </div>
        ) : (
          <EmptyState
            title="Your first listing could be here"
            body="Members haven't posted yet — be the first to share what you're selling or giving away."
            cta="Post a listing"
            to="/post/listing"
          />
        )}
      </section>

      {/* Urgent healthcare jobs */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 py-12">
        <div className="rounded-3xl bg-gradient-to-br from-[#F7F4EB] to-[#EFEAD9] border border-[#E2DED0] p-6 sm:p-10">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#C05A3D] font-semibold inline-flex items-center gap-2">
                <Stethoscope className="w-3 h-3" /> Healthcare hiring · Start this week
              </div>
              <h2 className="font-display text-3xl sm:text-4xl text-[#1E3A2F] mt-1">Immediate healthcare jobs</h2>
              <p className="text-sm text-[#5C5C5C] mt-2 max-w-xl">Caregivers, nurses, CNAs and medical staff — roles hiring right now across Washington.</p>
            </div>
            <div className="flex gap-2">
              <Link to="/jobs" data-testid="home-jobs-browse" className="rounded-full abm-btn-pine px-5 h-11 leading-[2.75rem] text-white text-sm">Browse jobs</Link>
              <Link to="/post/job" data-testid="home-jobs-post" className="rounded-full border border-[#1E3A2F] text-[#1E3A2F] px-5 h-11 leading-[2.6rem] text-sm hover:bg-[#1E3A2F] hover:text-white transition">Post a job</Link>
            </div>
          </div>
          {data?.urgent_jobs?.length ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {data.urgent_jobs.slice(0, 6).map((j) => <JobCard key={j.id} job={j} />)}
            </div>
          ) : (
            <div className="text-center py-12 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
              <Stethoscope className="w-8 h-8 text-[#C05A3D] mx-auto" />
              <div className="mt-3 font-display text-xl text-[#1E3A2F]">No jobs posted yet</div>
              <p className="text-sm text-[#5C5C5C] mt-1">Employers — post an opening and hire from the community.</p>
              <Link to="/post/job" className="inline-block mt-5 rounded-full abm-btn-primary px-5 h-10 leading-10 text-white text-sm">Post a job</Link>
            </div>
          )}
        </div>
      </section>

      {/* Events */}
      <section className="relative py-16 mt-6">
        <div className="absolute inset-0 -z-10 bg-[#1E3A2F]" />
        <div className="absolute inset-0 -z-10 opacity-[0.08]" style={{ backgroundImage: `url(${PATTERN_IMG})`, backgroundSize: "cover" }} />
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <div className="flex items-end justify-between mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-[#DDA74A] font-semibold">Coming up</div>
              <h2 className="font-display text-3xl text-[#F7F4EB] mt-1">Upcoming events</h2>
            </div>
            <Link to="/events" className="text-sm text-[#DDA74A] inline-flex items-center gap-1 hover:text-white">
              All events <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          {data?.upcoming_events?.length ? (
            <div className="grid md:grid-cols-2 gap-5">
              {data.upcoming_events.slice(0, 4).map((e) => <EventCard key={e.id} event={e} />)}
            </div>
          ) : (
            <div className="text-center py-14 border border-dashed border-white/20 rounded-2xl">
              <Users className="w-8 h-8 text-[#DDA74A] mx-auto" />
              <div className="mt-3 text-[#F7F4EB] font-display text-xl">No events yet — bring us together</div>
              <p className="text-sm text-[#A3B3AA] mt-1">Special, community and church events all live here.</p>
              <Link to="/post/event" className="inline-block mt-5 rounded-full abm-btn-primary px-5 h-10 leading-10">Post an event</Link>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-2xl bg-white border border-[#E2DED0] p-4">
      <div className="font-display text-2xl text-[#1E3A2F]">{value}</div>
      <div className="text-xs uppercase tracking-[0.15em] text-[#5C5C5C]">{label}</div>
    </div>
  );
}

function EmptyState({ title, body, cta, to }) {
  return (
    <div className="text-center py-14 border border-dashed border-[#E2DED0] rounded-2xl bg-white/60">
      <div className="font-display text-xl text-[#1E3A2F]">{title}</div>
      <p className="text-sm text-[#5C5C5C] mt-1 max-w-md mx-auto">{body}</p>
      <Link to={to} className="inline-block mt-5 rounded-full abm-btn-primary px-5 h-10 leading-10 text-white">{cta}</Link>
    </div>
  );
}
